import React from 'react';
import { Form, Popover } from 'antd';
import { CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { useGetFlowOutputs } from '@src/emp-3.0/api/queryHooks';

import {
  StyledDot,
  StyledIconWrapper,
  StyledList,
  StyledListItem,
  StyledListItemWrapper,
  StyleDotWrapper,
} from './styled';
import { IListItemContentProps } from './typed';
import { useNewDataModel } from '@hooks/useNewDataModel';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import Bulb from '@src/icons/bulb.svg';
import { useIsPatternMode } from '@src/pages/metric-templates/utils';

const IMG_CONFIG = {
  default: {
    width: 594,
    description: true,
  },
  'No Success': {
    width: 220,
    description: false,
  },
};

const ListItemContent = ({
  text,
  emphasisText,
  extraElements,
  popoverContent,
  enabledName,
  enabledItem,
}: IListItemContentProps) => (
  <StyledListItemWrapper>
    <StyleDotWrapper>
      {enabledItem && enabledName ? <CheckOutlined /> : <StyledDot />}
    </StyleDotWrapper>
    <StyledListItem checked={enabledName}>{text}</StyledListItem>
    <StyledListItem
      checked={enabledItem}
      as="span"
      style={{ fontWeight: 700, whiteSpace: 'nowrap' }}
    >
      {emphasisText}
    </StyledListItem>
    {extraElements}
    {popoverContent && (
      <Popover title={null} content={popoverContent} placement="rightBottom">
        <QuestionCircleOutlined style={{ opacity: '0.4', fontSize: '18px' }} />
      </Popover>
    )}
  </StyledListItemWrapper>
);

const FlowOutput = () => {
  const isNewDataModel = useNewDataModel();
  const { data: flowOutputsData = [], isLoading: isLoadingGetFlowOutputs } = useGetFlowOutputs();
  const { formInstance } = useFlowStore();
  const { isPatternMode } = useIsPatternMode();
  const name = Form.useWatch('name', formInstance);
  const paring_logic = Form.useWatch(['definition', 'pairing_logic'], formInstance);
  const start_event = Form.useWatch(['definition', 'events', 'start_event'], formInstance);
  const success_event = Form.useWatch(['definition', 'events', 'success_event'], formInstance);
  const error_event = Form.useWatch(['definition', 'events', 'error_event'], formInstance);
  const network_request_patterns = Form.useWatch(
    ['definition', 'network_request_patterns'],
    formInstance,
  );
  const networkRequestRelatedMetrics = [
    'Network Request Avg Duration',
    'Network Request Failure',
    'Network Request Count',
  ];
  const flowSqlDefinition = Form.useWatch(['definition', 'flow_sql_definition'], formInstance);

  const hasValidErrorEvents =
    Array.isArray(error_event) && error_event.filter((item) => item != null).length > 0;
  const hasNetworkRequestPatterns =
    Array.isArray(network_request_patterns) && network_request_patterns.length > 0;

  const checkItemReady = (outputName) => {
    let ready = !!(success_event && start_event) || (isPatternMode && flowSqlDefinition);
    // error metrics are always created for pattern mode
    if (outputName.includes('Error') && !isPatternMode) {
      ready = ready && hasValidErrorEvents;
    }
    if (networkRequestRelatedMetrics.includes(outputName)) {
      ready = ready && hasNetworkRequestPatterns;
    }
    return ready;
  };

  return (
    <>
      <div className="output-title">
        <Bulb />
        <span>
          Deploying a {isPatternMode ? 'pattern' : ' flow'} creates these metrics for{' '}
          {isPatternMode ? 'pattern' : ' flow'} analysis:
        </span>
      </div>
      {!isLoadingGetFlowOutputs ? (
        <StyledList>
          {flowOutputsData.map((item) => {
            const imgConfig = IMG_CONFIG[item.name] || IMG_CONFIG.default;
            const isPercentage = isNewDataModel ? item.is_percentage_new_model : item.is_percentage;
            const isItemReady = checkItemReady(item.name);
            const hasImage = !!item.pic || !!item.pic_lf;
            const imageSrc = paring_logic === 'last_first' ? item.pic_lf || item.pic : item.pic;
            const defaultName = isPatternMode ? 'Pattern Name' : 'Flow Name';
            return (
              <ListItemContent
                key={item.name}
                text={name || defaultName}
                emphasisText={item.name}
                popoverContent={
                  hasImage ? (
                    <div style={{ position: 'relative' }}>
                      <img src={imageSrc} alt="" style={{ width: imgConfig.width }} />
                    </div>
                  ) : null
                }
                enabledName={!!name}
                enabledItem={isItemReady}
                extraElements={
                  <>
                    {/*confirmed with UX designer Zhengyan */}
                    {item.is_number && isPercentage && <StyledIconWrapper>#</StyledIconWrapper>}
                    {isPercentage && <StyledIconWrapper>%</StyledIconWrapper>}
                  </>
                }
              />
            );
          })}
        </StyledList>
      ) : null}
    </>
  );
};

export default FlowOutput;
